import React from "react";
import { Box, Typography, IconButton, Divider, alpha } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import {
  X,
  ChevronRight,
  Brain,
  BicepsFlexed,
  Smile,
  Clock,
} from "lucide-react";

import { FEEL_MAP } from "../training-summary/TrainingSummaryWellBegin";
import { colorSystem } from "../../theme";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.04)
        : colorSystem.blue5,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(2),
    cursor: "pointer",
    transition: "background-color 0.2s ease, border-color 0.2s ease",
    width: "100%",
    border:
      theme.palette.mode === "dark"
        ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
        : "1px solid transparent",
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.white, 0.08)
          : alpha(colorSystem.blue5, 0.75),
      borderColor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.white, 0.16)
          : alpha(colorSystem.gray4, 0.6),
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  titleBlock: {
    display: "flex",
    flexDirection: "column",
  },
  divide: {
    marginTop: theme.spacing(1.5),
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.12)
        : undefined,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  footerItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1),
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.1)
        : theme.palette.common.white,
    color:
      theme.palette.mode === "dark"
        ? theme.palette.text.primary
        : theme.palette.text.secondary,
    borderRadius: theme.spacing(2.5),
    fontSize: "0.75rem",
    fontWeight: 500,
    boxShadow:
      theme.palette.mode === "dark"
        ? "none"
        : `0 1px 3px ${alpha(theme.palette.grey[400], 0.25)}`,
  },
  icon: {
    width: 16,
    height: 16,
    color:
      theme.palette.mode === "dark"
        ? theme.palette.text.primary
        : theme.palette.text.secondary,
  },
  actionButton: {
    color: theme.palette.primary.main,
    padding: theme.spacing(0.5),
    width: 32,
    height: 32,
  },
  mindIconColor: {
    color:
      theme.palette.mode === "dark"
        ? theme.palette.primary.light
        : theme.palette.primary.main,
  },
  bodyIconColor: {
    color:
      theme.palette.mode === "dark"
        ? theme.palette.info.light
        : colorSystem.blue1,
  },
  feelIconColor: {
    color:
      theme.palette.mode === "dark"
        ? theme.palette.activity.habit
        : theme.palette.activity.habit,
  },
  timeIconColor: {
    color: theme.palette.text.primary,
  },
}));

export type WorkoutCardBaseProps = {
  title: string;
  subtitle?: React.ReactNode;
  onClick?: () => void;
  type?: "arrow" | "close" | "none";
  showDivider?: boolean;
  onClose?: () => void;
  footerStats?: {
    mind?: string;
    body?: string;
    feel?: string;
    time?: number | null;
  };
  footerActions?: React.ReactNode;
  className?: string;
  sx?: object;
};

const WorkoutCardBase: React.FC<WorkoutCardBaseProps> = ({
  title,
  subtitle,
  onClick,
  type = "arrow",
  showDivider = true,
  onClose,
  footerStats,
  footerActions,
  className,
  sx,
}) => {
  const s = useStyles();

  const hasFooterStats =
    Boolean(footerStats?.mind) ||
    Boolean(footerStats?.body) ||
    Boolean(footerStats?.feel) ||
    Boolean(footerStats?.time);

  const shouldShowDivider = showDivider && hasFooterStats;

  const styleAfter = (value: string) => {
    return FEEL_MAP[value]?.class ? s[FEEL_MAP[value].class] : "";
  };

  const FeelIcon = ({ value, ...props }) => {
    const Icon = FEEL_MAP[value]?.icon || Smile;
    return <Icon {...props} />;
  };

  return (
    <Box className={clsx(s.root, className)} sx={sx} onClick={onClick}>
      <Box className={s.header}>
        <Box className={s.titleBlock}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>

          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        {type === "arrow" && <ChevronRight className={s.actionButton} />}
        {type === "close" && (
          <IconButton
            className={s.actionButton}
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            size="small"
          >
            <X size={20} strokeWidth={2} />
          </IconButton>
        )}
      </Box>

      {shouldShowDivider && <Divider className={s.divide} />}

      {hasFooterStats && (
        <Box className={s.footer}>
          {footerStats?.mind && (
            <Typography variant="body2" className={s.footerItem}>
              <Brain className={clsx(s.icon, s.mindIconColor)} />
              {footerStats.mind}
            </Typography>
          )}

          {footerStats?.body && (
            <Typography variant="body2" className={s.footerItem}>
              <BicepsFlexed className={clsx(s.icon, s.bodyIconColor)} />
              {footerStats.body}
            </Typography>
          )}

          {footerStats?.feel && (
            <Typography
              className={clsx(s.footerItem, styleAfter(footerStats.feel))}
            >
              <FeelIcon
                value={footerStats.feel}
                className={clsx(s.icon, s.feelIconColor)}
              />
              {footerStats.feel}
            </Typography>
          )}

          {footerStats?.time && (
            <Typography variant="body2" className={s.footerItem}>
              <Clock className={clsx(s.icon, s.timeIconColor)} />
              {footerStats.time} min
            </Typography>
          )}

          {footerActions && <Box sx={{ ml: "auto" }}>{footerActions}</Box>}
        </Box>
      )}
    </Box>
  );
};

export default WorkoutCardBase;
